import React, { useState, useEffect } from 'react';
import { Effort } from '../../../types/Effort';
import { Task, TaskType, TaskStage } from '../../../types/Task';
import { Project } from '../../../types/Project';
import { ForemanArea } from '../../../types/ForemanArea';
import GenericTaskModal from '../../../components/GenericTaskModal';
import RoutineTaskModal from '../../../components/RoutineTaskModal';
import TaskTypeSelector from './TaskTypeSelector';
import TaskModal from '../../../components/QPTaskModal';
import { ContractArea } from '../../../types/ContractArea';
import { RoutineTask } from '../../../types/RoutineTask';
import TaskEffortsTable from '../../../components/TaskEffortsTable';
import CustomTaskModal from '../../../components/CustomTaskModal';
import { routineTaskService } from '../../../services/routineTaskService';
import CloseRoutineTaskModal from './CloseRoutineTaskModal';
import CancelTaskConfirmationModal from '../../../components/CancelTaskConfirmationModal';
import ConvertTaskTypeModal, { ConvertDirection } from './ConvertTaskTypeModal';
import { usePermissions } from '../../../hooks/usePermissions';
import { taskService } from '../../../services/taskService';
import { useAuth } from '../../../contexts/AuthContext';
import TaskHistoryModal from '../../../components/TaskHistoryModal';
import { taskService as quantifiedTaskService } from '../../../services/quantifiedTaskService';
import InfoTooltip from '../../../components/InfoTooltip';
import CloneTaskConfirmModal from '../../../components/CloneTaskConfirmModal';
import PrintTaskPDFModal from '../../quantified-tracking/components/PrintTaskPDFModal';
import CompletionModal from '../../quantified-tracking/components/taskModals/CompletionModal';
import CloseTaskModal from '../../quantified-tracking/components/taskModals/CloseTaskModal';
import ReopenTaskModal from '../../quantified-tracking/components/taskModals/ReopenTaskModal';
import SetInternalReviewModal from '../../quantified-tracking/components/taskModals/SetInternalReviewModal';
import { useResizableColumns } from '../../../hooks/useResizableColumns';
import { ResizeHandle } from '../../../components/ResizeHandle';
import { Button, Form, InputGroup, Alert, Badge, RowActions, Table, SortableTh, StatusBadge, EmptyValue, IdText } from '@/components/ui';
import { describeActionError } from '@/utils/loadError';

// Task Actions Dropdown Component
interface TaskActionsDropdownProps {
  task: Task;
  canEditTask: boolean;
  canCloneTask: boolean;
  canCloseTask: boolean;
  canReopenTask: boolean;
  canCancelTask: boolean;
  onViewHistory: () => void;
  onEditTask: () => void;
  onCloneTask: () => void;
  onCloseTask: () => void;
  onReopenTask: () => void;
  onCancelTask: () => void;
  onPrintPDF: () => void;
  onConvertToQuantified?: () => void;
  onConvertToCustom?: () => void;
  onReportAccomplishment?: () => void;
  onSetInternalReview?: () => void;
  onSetActive?: () => void;
}

export const TaskActionsDropdown: React.FC<TaskActionsDropdownProps> = ({
  task,
  canEditTask,
  canCloneTask,
  canCloseTask,
  canReopenTask,
  canCancelTask,
  onViewHistory,
  onEditTask,
  onCloneTask,
  onCloseTask,
  onReopenTask,
  onCancelTask,
  onPrintPDF,
  onConvertToQuantified,
  onConvertToCustom,
  onReportAccomplishment,
  onSetInternalReview,
  onSetActive
}) => {
  const { hasPermission } = usePermissions();

  // The menu itself is RowActions (was ~180 lines of raw <button>, hand-rolled fixed-position
  // menu with its own viewport flip/clamp math, a full-screen click-catcher, and no keyboard
  // or ARIA support - the shared Dropdown provides all of that).
  //
  // The gating below is master's, ported verbatim: quantified tasks are driven by stage +
  // per-stage permission rather than by the generic canXTask flags, which is what makes
  // closing a quantified task from this tab behave like the QP screen.
  const isQuantified = task.type === 'Quantified';
  const status = task.status;

  const showReportAccomplishment =
    isQuantified &&
    (status === TaskStage.ACTIVE || status === TaskStage.ACTIVE_MARKED_AS_COMPLETE) &&
    hasPermission('quantified:accomplishment_submit') &&
    !!onReportAccomplishment;

  const showQuantifiedClose =
    isQuantified &&
    status === TaskStage.INTERNAL_REVIEW &&
    hasPermission('quantified:review_manage');

  const showRoutineClose =
    task.type === 'Routine' && status === TaskStage.ACTIVE && canCloseTask;

  const showQuantifiedReopen =
    isQuantified &&
    (status === TaskStage.CLOSED || status === TaskStage.FINALIZED) &&
    hasPermission('quantified:closed_manage');

  const showNonQuantifiedReopen =
    !isQuantified && status === TaskStage.CLOSED && canReopenTask;

  const showSetInternalReview =
    isQuantified &&
    (status === TaskStage.CLOSED || status === TaskStage.FINALIZED) &&
    hasPermission('quantified:closed_manage') &&
    !!onSetInternalReview;

  const showSetActive =
    isQuantified &&
    !!onSetActive &&
    (
      ((status === TaskStage.ACTIVE_MARKED_AS_COMPLETE || status === TaskStage.INTERNAL_REVIEW) &&
        hasPermission('quantified:active_manage')) ||
      (status === TaskStage.PLANNED && hasPermission('quantified:planned_manage')) ||
      (status === TaskStage.PROPOSED && hasPermission('quantified:proposed_manage'))
    );

  const quantifiedCancelStatuses = [
    TaskStage.PROPOSED,
    TaskStage.PLANNED,
    TaskStage.ACTIVE,
    TaskStage.ACTIVE_MARKED_AS_COMPLETE,
    TaskStage.INTERNAL_REVIEW
  ];
  const showQuantifiedCancel =
    isQuantified &&
    quantifiedCancelStatuses.includes(status as TaskStage) &&
    hasPermission('quantified:cancelled_manage');

  const showNonQuantifiedCancel =
    !isQuantified && task.status !== 'CANCELLED' && canCancelTask;

  return (
    <RowActions
      align="left"
      className="tw-justify-center"
      actions={[
        { kind: 'edit', label: 'Edit Task', onClick: onEditTask, hidden: !canEditTask },
        {
          kind: 'custom',
          icon: 'bi-copy',
          label: 'Clone Task',
          onClick: onCloneTask,
          hidden: !canCloneTask || task.status === 'CANCELLED',
        },
        { kind: 'custom', icon: 'bi-file-earmark-pdf', label: 'Print PDF', onClick: onPrintPDF },
        { kind: 'history', label: 'Task History', onClick: onViewHistory },
        {
          kind: 'custom',
          icon: 'bi-check2-circle',
          label: 'Report Accomplishment',
          onClick: () => onReportAccomplishment?.(),
          hidden: !showReportAccomplishment,
        },
        {
          kind: 'custom',
          icon: 'bi-play-circle',
          label: 'Set Active',
          onClick: () => onSetActive?.(),
          hidden: !showSetActive,
        },
        {
          kind: 'custom',
          // A quantified close is a lock (finalising a reviewed task), not a tick.
          icon: showQuantifiedClose ? 'bi-lock' : 'bi-check-circle',
          label: 'Close Task',
          onClick: onCloseTask,
          hidden: !(showRoutineClose || showQuantifiedClose),
        },
        {
          kind: 'custom',
          icon: 'bi-arrow-counterclockwise',
          label: 'Reopen Task',
          onClick: onReopenTask,
          hidden: !(showQuantifiedReopen || showNonQuantifiedReopen),
        },
        {
          kind: 'custom',
          icon: 'bi-arrow-return-left',
          label: 'Set to Internal Review',
          onClick: () => onSetInternalReview?.(),
          hidden: !showSetInternalReview,
        },
        {
          kind: 'cancel',
          label: 'Cancel Task',
          onClick: onCancelTask,
          hidden: !(showQuantifiedCancel || showNonQuantifiedCancel),
        },
        {
          kind: 'custom',
          icon: 'bi-arrow-repeat',
          label: 'Convert to Quantified',
          onClick: () => onConvertToQuantified?.(),
          hidden: !(canEditTask && task.type === 'Custom' && onConvertToQuantified),
        },
        {
          kind: 'custom',
          icon: 'bi-arrow-repeat',
          label: 'Convert to Custom',
          onClick: () => onConvertToCustom?.(),
          hidden: !(canEditTask && task.type === 'Quantified' && onConvertToCustom),
        },
      ]}
    />
  );
};

export type ProjectTaskSavedOptions = {
  successToast?: string;
  isNew?: boolean;
  keepOpen?: boolean;
};

interface ProjectTasksTabProps {
  project: Project;
  onTaskSaved: (
    task: Task | RoutineTask,
    options?: ProjectTaskSavedOptions
  ) => void | Promise<void>;
  foremanAreas: ForemanArea[];
  onTaskAction?: (action: string, task: Task) => void;
  listContainerRef?: React.RefObject<HTMLDivElement>;
}

const ProjectTasksTab: React.FC<ProjectTasksTabProps> = ({ 
  project, 
  onTaskSaved,
  foremanAreas,
  onTaskAction,
  listContainerRef
}) => {
  const { hasPermission } = usePermissions();
  const { currentUser } = useAuth();
  const canCreateTask = hasPermission('project:task_create');
  const projectOpenForNewTasks = project.status?.toLowerCase() !== 'completed';
  const canAddTaskToThisProject = canCreateTask && projectOpenForNewTasks;
  const canEditTask = hasPermission('project:task_edit');
  const canToggleVehicleFuel = hasPermission('quantified:task_edit');
  const [, setFuelEffortRenderBump] = useState(0);
  const handleVehicleFuelEffortPatched = (effort: Effort) => {
    if (effort.taskId == null || !project.tasks?.length) return;
    const updatedTasks = project.tasks.map((t) =>
      t.id !== effort.taskId
        ? t
        : {
            ...t,
            efforts: (t.efforts || []).map((e) => (e.id === effort.id ? { ...e, ...effort } : e))
          }
    );
    project.tasks = updatedTasks;
    setFuelEffortRenderBump((n) => n + 1);
  };
  const canCloseTask = hasPermission('project:task_close');
  const canReopenTask = hasPermission('project:task_close');
  const canCancelTask = hasPermission('project:task_cancel') || hasPermission('project:task_edit');
  const canCloneTask = canCreateTask || canEditTask;

  // Debug logging
  console.log('Permission check:', {
    canCancelTask,
    hasCancelPermission: hasPermission('project:task_cancel'),
    hasEditPermission: hasPermission('project:task_edit')
  });

  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showRoutineTaskModal, setShowRoutineTaskModal] = useState(false);
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [selectedTaskType, setSelectedTaskType] = useState<TaskType>('Overhead');
  const [searchTerm, setSearchTerm] = useState('');
  // A failed row action (set active, reopen, clone, cancel) is shown inline above the list (STATE-06).
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [selectedRoutineTask, setSelectedRoutineTask] = useState<RoutineTask | null>(null);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [contractAreas, setContractAreas] = useState<ContractArea[]>([]);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [filterTaskType, setFilterTaskType] = useState<TaskType | ''>('');
  const [showCanceledTasks, setShowCanceledTasks] = useState(false);
  const [showCustomTaskModal, setShowCustomTaskModal] = useState(false);
  const [isCloseTaskModalVisible, setIsCloseTaskModalVisible] = useState(false);
  const [selectedTaskForClose, setSelectedTaskForClose] = useState<Task | null>(null);
  const [isQuantifiedCloseModalVisible, setIsQuantifiedCloseModalVisible] = useState(false);
  const [selectedTaskForQuantifiedClose, setSelectedTaskForQuantifiedClose] = useState<Task | null>(null);
  const [isCompletionModalVisible, setIsCompletionModalVisible] = useState(false);
  const [selectedTaskForCompletion, setSelectedTaskForCompletion] = useState<Task | null>(null);
  const [isReopenTaskModalVisible, setIsReopenTaskModalVisible] = useState(false);
  const [selectedTaskForReopen, setSelectedTaskForReopen] = useState<Task | null>(null);
  const [isSetInternalReviewModalVisible, setIsSetInternalReviewModalVisible] = useState(false);
  const [selectedTaskForInternalReview, setSelectedTaskForInternalReview] = useState<Task | null>(null);
  const [isCancelTaskModalVisible, setIsCancelTaskModalVisible] = useState(false);
  const [selectedTaskForCancel, setSelectedTaskForCancel] = useState<Task | null>(null);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [selectedTaskForHistory, setSelectedTaskForHistory] = useState<Task | null>(null);
  const [isConvertModalVisible, setIsConvertModalVisible] = useState(false);
  const [selectedTaskForConvert, setSelectedTaskForConvert] = useState<Task | null>(null);
  const [convertDirection, setConvertDirection] = useState<ConvertDirection | null>(null);
  const [cloneConfirmTask, setCloneConfirmTask] = useState<Task | null>(null);
  const [printPdfTask, setPrintPdfTask] = useState<Task | null>(null);

  const RESIZABLE_COLUMN_IDS = ['taskNumber', 'name', 'status', 'type', 'coding', 'activity', 'actions'];
  const defaultWidths: Record<string, string> = {
    taskNumber: '180px',
    name: '360px',
    status: '180px',
    type: '180px',
    coding: '180px',
    activity: '180px',
    actions: '120px'
  };
  const { getColumnWidth, getColumnWidthPx, startResize } = useResizableColumns(
    RESIZABLE_COLUMN_IDS,
    { defaultWidths }
  );

  const tableWrapperRef = React.useRef<HTMLDivElement>(null);
  const stickyScrollbarRef = React.useRef<HTMLDivElement>(null);
  const isScrollingSyncRef = React.useRef(false);
  const scrollSyncRafRef = React.useRef<number | null>(null);
  const lastScrollSourceRef = React.useRef<'table' | 'bar' | null>(null);
  const [stickyScrollWidth, setStickyScrollWidth] = React.useState(0);

  React.useEffect(() => {
    const wrapper = tableWrapperRef.current;
    // The shared <Table> primitive isn't a forwardRef component, so the table element itself
    // isn't reachable via ref — it's the wrapper's only child, which is equivalent for sizing.
    const table = wrapper?.firstElementChild;
    if (!wrapper || !table) return;
    const updateStickyWidth = () => setStickyScrollWidth(wrapper.scrollWidth);
    updateStickyWidth();
    const ro = new ResizeObserver(updateStickyWidth);
    ro.observe(table);
    return () => ro.disconnect();
  }, [project.tasks?.length, getColumnWidth]);

  const runScrollSync = React.useCallback(() => {
    const src = lastScrollSourceRef.current;
    const tableEl = tableWrapperRef.current;
    const barEl = stickyScrollbarRef.current;
    if (src === 'table' && barEl && tableEl) {
      isScrollingSyncRef.current = true;
      barEl.scrollLeft = tableEl.scrollLeft;
      requestAnimationFrame(() => { isScrollingSyncRef.current = false; });
    } else if (src === 'bar' && tableEl && barEl) {
      isScrollingSyncRef.current = true;
      tableEl.scrollLeft = barEl.scrollLeft;
      requestAnimationFrame(() => { isScrollingSyncRef.current = false; });
    }
  }, []);

  const handleTableWrapperScroll = React.useCallback(() => {
    if (isScrollingSyncRef.current) return;
    lastScrollSourceRef.current = 'table';
    if (scrollSyncRafRef.current === null) {
      scrollSyncRafRef.current = requestAnimationFrame(() => {
        scrollSyncRafRef.current = null;
        runScrollSync();
      });
    }
  }, [runScrollSync]);

  const handleStickyScroll = React.useCallback(() => {
    if (isScrollingSyncRef.current) return;
    lastScrollSourceRef.current = 'bar';
    if (scrollSyncRafRef.current === null) {
      scrollSyncRafRef.current = requestAnimationFrame(() => {
        scrollSyncRafRef.current = null;
        runScrollSync();
      });
    }
  }, [runScrollSync]);

  const getAvailableTaskTypes = (): TaskType[] => {
    switch (project.type) {
      case 'H200':
        return ['Quantified', 'Routine', 'Overhead', 'Custom'];
      case 'Quantified':
        return ['Overhead', 'Quantified', 'Custom'];
      case 'Routine':
        return ['Overhead', 'Routine', 'Custom'];
      case 'Internal':
        return ['Overhead', 'Custom'];
      case 'Other Revenue':
        return ['Overhead', 'Custom'];
      case 'CMC/MVI':
        return ['Quantified', 'Routine', 'Overhead', 'Custom'];
      default:
        return ['Overhead'];
    }
  };

  const handleAddTask = () => {
    setShowTypeSelector(true);
  };

  const handleTaskTypeSelected = (type: TaskType) => {
    setSelectedTaskType(type);
    setShowTypeSelector(false);
    
    switch (type) {
      case 'Routine':
        setShowRoutineTaskModal(true);
        break;
      case 'Custom':
        setShowCustomTaskModal(true);
        break;
      default:
        setShowTaskModal(true);
        break;
    }
  };

  const handleTaskSaved = (task: Task | RoutineTask, options?: ProjectTaskSavedOptions) => {
    const isNew = options?.isNew ?? (!selectedTask && !selectedRoutineTask);

    if (options?.keepOpen) {
      if ('labourAllocation' in task) {
        setSelectedRoutineTask(task as RoutineTask);
      } else {
        setSelectedTask(task as Task);
      }
      onTaskSaved(task, { ...options, isNew: false, keepOpen: true });
      return;
    }

    const savedProjectId = (task as Task).projectId ?? (task as RoutineTask).projectId;
    const taskMovedToOtherProject = savedProjectId !== undefined && savedProjectId !== project.id;

    if (taskMovedToOtherProject) {
      setShowTaskModal(false);
      setShowRoutineTaskModal(false);
      setShowCustomTaskModal(false);
      setSelectedTask(null);
      setSelectedRoutineTask(null);
    } else if (selectedTask && (task as Task).id === selectedTask.id) {
      setSelectedTask(task as Task);
    } else if (selectedRoutineTask && 'labourAllocation' in task && (task as RoutineTask).id === selectedRoutineTask.id) {
      setSelectedRoutineTask(task as RoutineTask);
    } else {
      setShowTaskModal(false);
      setShowRoutineTaskModal(false);
      setShowCustomTaskModal(false);
      setSelectedTask(null);
      setSelectedRoutineTask(null);
    }

    onTaskSaved(task, { ...options, isNew });
  };

  const handleEditTask = (task: Task | RoutineTask) => {
    if (onTaskAction) {
      onTaskAction('edit', task as Task);
    } else {
      if ('labourAllocation' in task) {
        setSelectedRoutineTask(task);
        setShowRoutineTaskModal(true);
      } else if ((task as Task).type === 'Custom') {
        setSelectedTask(task as Task);
        setShowCustomTaskModal(true);
      } else {
        setSelectedTask(task as Task);
        setShowTaskModal(true);
      }
    }
  };

  // SortableTh already computes the next direction (toggle on the active column, else asc) and
  // hands it back as `dir`, so this just commits it — same toggle behaviour as the old
  // handleSort/getSortIcon pair, minus the duplicated icon markup.
  const handleSort = (field: string, dir: 'asc' | 'desc') => {
    setSortField(field);
    setSortDirection(dir);
  };

  const sortTasks = (tasksToSort: Task[]) => {
    if (!sortField) return tasksToSort;

    return [...tasksToSort].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'taskNumber':
          aValue = a.taskNumber || '';
          bValue = b.taskNumber || '';
          break;
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'description':
          aValue = a.description || '';
          bValue = b.description || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'type':
          aValue = a.type || '';
          bValue = b.type || '';
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  };

  const filteredTasks = project.tasks?.filter(task => {
    const matchesSearch = (
      (task.description?.toLowerCase() || '').includes(searchTerm.trim().toLowerCase()) ||
      (task.taskNumber?.toLowerCase() || '').includes(searchTerm.trim().toLowerCase()) ||
      (task.name?.toLowerCase() || '').includes(searchTerm.trim().toLowerCase())
    );

    const matchesType = !filterTaskType || task.type === filterTaskType;
    
    // Handle cancelled tasks visibility
    const isCancelled = task.status === 'CANCELLED';
    const matchesCancelledFilter = showCanceledTasks || !isCancelled;

    return matchesSearch && matchesType && matchesCancelledFilter;
  }) || [];

  const sortedTasks = sortTasks(filteredTasks);

  const toggleEfforts = (taskId: number) => {
    setExpandedRows(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  // Get default task status based on project status
  const getDefaultTaskStatus = (): string => {
    return project.status.toUpperCase() === 'ACTIVE' ? 'ACTIVE' : 'PLANNED';
  };

  const applyLocalTaskStatus = (task: Task, status: string, extra?: Partial<Task>) => {
    const updated = { ...task, status, ...extra };
    const updatedTasks = project.tasks?.map(t =>
      t.id === task.id ? { ...t, ...updated } : t
    ) || [];
    project.tasks = updatedTasks;
    onTaskSaved(updated);
  };

  const handleCloseTask = async (task: Task) => {
    if (task.type === 'Quantified') {
      setSelectedTaskForQuantifiedClose(task);
      setIsQuantifiedCloseModalVisible(true);
      return;
    }
    setSelectedTaskForClose(task);
    setIsCloseTaskModalVisible(true);
  };

  const handleReportAccomplishment = (task: Task) => {
    setSelectedTaskForCompletion(task);
    setIsCompletionModalVisible(true);
  };

  const handleSetInternalReview = (task: Task) => {
    setSelectedTaskForInternalReview(task);
    setIsSetInternalReviewModalVisible(true);
  };

  const handleSetActive = async (task: Task) => {
    try {
      setActionError(null);
      if (!currentUser?.id) throw new Error('User not authenticated');
      await quantifiedTaskService.updateTaskStatus(task.id, {
        status: TaskStage.ACTIVE,
        comments: 'Task set to active from Projects',
        userId: currentUser.id,
        taskId: task.id
      });
      applyLocalTaskStatus(task, TaskStage.ACTIVE);
    } catch (error) {
      console.error('Error setting task to active:', error);
      setActionError(describeActionError(error, "Couldn't set the task to active. Try again."));
    }
  };

  const handleReopenTask = async (task: Task) => {
    if (task.type === 'Quantified') {
      setSelectedTaskForReopen(task);
      setIsReopenTaskModalVisible(true);
      return;
    }
    try {
      setActionError(null);
      if (task.type === 'Routine') {
        await routineTaskService.updateTaskStatus(task.id, 'ACTIVE', 'Reopened from Projects');
      } else if (task.type === 'Custom' || task.type === 'Overhead') {
        await taskService.updateTask(task.id.toString(), {
          description: task.description || '',
          type: task.type,
          status: 'ACTIVE',
          projectId: task.projectId ?? task.project?.id ?? 0,
          name: task.name,
          foremanAreaId: task.foremanAreaId ?? undefined
        });
      } else {
        return;
      }
      applyLocalTaskStatus(task, 'ACTIVE');
    } catch (error) {
      console.error('Error reopening task:', error);
      setActionError(describeActionError(error, "Couldn't reopen the task. Try again."));
    }
  };

  const handleConfirmQuantifiedClose = async (comments: string) => {
    if (!selectedTaskForQuantifiedClose) return;
    await quantifiedTaskService.closeTask(selectedTaskForQuantifiedClose.id, {
      comments: comments || 'Task closed',
      userId: currentUser?.id,
      taskId: selectedTaskForQuantifiedClose.id
    });
    applyLocalTaskStatus(selectedTaskForQuantifiedClose, TaskStage.CLOSED);
    setIsQuantifiedCloseModalVisible(false);
    setSelectedTaskForQuantifiedClose(null);
  };

  const handleConfirmQuantifiedReopen = async (comments: string) => {
    if (!selectedTaskForReopen) return;
    await quantifiedTaskService.reopenTask(selectedTaskForReopen.id, {
      comments,
      userId: currentUser?.id,
      taskId: selectedTaskForReopen.id
    });
    applyLocalTaskStatus(selectedTaskForReopen, TaskStage.ACTIVE);
    setIsReopenTaskModalVisible(false);
    setSelectedTaskForReopen(null);
  };

  const handleInternalReviewSubmitted = () => {
    if (!selectedTaskForInternalReview) return;
    applyLocalTaskStatus(selectedTaskForInternalReview, TaskStage.INTERNAL_REVIEW);
    setIsSetInternalReviewModalVisible(false);
    setSelectedTaskForInternalReview(null);
  };

  const handleCompletionDone = () => {
    if (!selectedTaskForCompletion) return;
    applyLocalTaskStatus(selectedTaskForCompletion, TaskStage.INTERNAL_REVIEW);
    setIsCompletionModalVisible(false);
    setSelectedTaskForCompletion(null);
  };

  const handleConvertToQuantified = (task: Task) => {
    setSelectedTaskForConvert(task);
    setConvertDirection('toQuantified');
    setIsConvertModalVisible(true);
  };

  const handleConvertToCustom = (task: Task) => {
    setSelectedTaskForConvert(task);
    setConvertDirection('toCustom');
    setIsConvertModalVisible(true);
  };

  const handleCloneTask = async (
    task: Task,
    nameOverride?: string,
    quantifiedOptions?: { contractYear?: string; status?: string }
  ) => {
    try {
      setActionError(null);
      const options: { name?: string; contractYear?: string; status?: string } = {};
      if (nameOverride != null && nameOverride.trim() !== '') {
        options.name = nameOverride.trim();
      }
      if (task.type === 'Quantified' && quantifiedOptions) {
        if (quantifiedOptions.contractYear?.trim()) {
          options.contractYear = quantifiedOptions.contractYear.trim();
        }
        if (quantifiedOptions.status?.trim()) {
          options.status = quantifiedOptions.status.trim();
        }
      }
      const cloned = await taskService.cloneTask(
        task.id,
        Object.keys(options).length > 0 ? options : undefined
      );
      const from = task.taskNumber || `#${task.id}`;
      const created = cloned.taskNumber || `#${cloned.id}`;
      const needle = (cloned.taskNumber || String(cloned.id)).trim();

      await Promise.resolve(
        onTaskSaved(cloned as Task, {
          successToast: `New task ${created} – cloned from ${from}. Search is set to the new task; edit form opened.`,
          isNew: true,
        })
      );

      setSearchTerm(needle);

      const t = cloned as Task;
      if (t.type === 'Routine') {
        const details = await routineTaskService.getTaskDetails(cloned.id);
        setSelectedRoutineTask(details);
        setShowRoutineTaskModal(true);
      } else if (t.type === 'Custom') {
        setSelectedTask(t);
        setShowCustomTaskModal(true);
      } else {
        setSelectedTask(t);
        setShowTaskModal(true);
      }
    } catch (error) {
      console.error('Error cloning task:', error);
      setActionError(describeActionError(error, "Couldn't clone the task. Try again."));
    }
  };

  const handleConfirmConvert = async (payload: {
    targetType: 'Quantified' | 'Custom';
    unitOfMeasure?: string;
    identifiedQuantity?: number;
  }) => {
    if (!selectedTaskForConvert) return;
    const updated = await taskService.convertTaskType(selectedTaskForConvert.id, payload);
    onTaskSaved(updated);
    if (payload.targetType === 'Quantified') {
      setSelectedTask(updated as Task);
      setShowTaskModal(true);
    }
    setSelectedTaskForConvert(null);
    setConvertDirection(null);
  };

  const handleConfirmClose = async (comments: string) => {
    try {
      if (selectedTaskForClose?.type === 'Routine') {
        await routineTaskService.updateTaskStatus(selectedTaskForClose.id, 'CLOSED', 'Closed from Projects');
        // Update the task in the project's tasks array
        const updatedTasks = project.tasks?.map(t => 
          t.id === selectedTaskForClose.id ? { ...t, status: 'CLOSED' } : t
        ) || [];
        project.tasks = updatedTasks;
        // Trigger a re-render
        onTaskSaved(selectedTaskForClose);
      }
    } catch (error) {
      console.error('Error closing task:', error);
    } finally {
      setIsCloseTaskModalVisible(false);
      setSelectedTaskForClose(null);
    }
  };

  const handleCancelTask = (task: Task) => {
    setSelectedTaskForCancel(task);
    setIsCancelTaskModalVisible(true);
  };

  const handleViewHistory = async (task: Task) => {
    try {
      console.log('Loading task history for task:', {
        id: task.id,
        taskNumber: task.taskNumber,
        type: task.type,
        currentHistoryCount: task.taskHistory?.length || 0
      });

      // Refresh the task to get the latest history
      let refreshedTask: Task;
      
      if (task.type === 'Routine') {
        // For routine tasks, use routineTaskService
        const routineTask = await routineTaskService.getTaskDetails(task.id);
        console.log('Routine task history loaded:', {
          taskId: task.id,
          historyCount: routineTask.taskHistory?.length || 0
        });
        refreshedTask = {
          ...task,
          taskHistory: routineTask.taskHistory || []
        } as Task;
      } else {
        // For other task types, try to get from quantifiedTaskService or regular taskService
        try {
          refreshedTask = await quantifiedTaskService.getTask(task.id);
          console.log('Quantified task history loaded:', {
            taskId: task.id,
            historyCount: refreshedTask.taskHistory?.length || 0
          });
        } catch (error) {
          console.log('Quantified task service failed, trying regular task service:', error);
          // If that fails, try regular taskService
          try {
            const regularTask = await taskService.getTask(task.id);
            console.log('Regular task history loaded:', {
              taskId: task.id,
              historyCount: regularTask.taskHistory?.length || 0,
              hasTaskHistory: !!regularTask.taskHistory
            });
            refreshedTask = {
              ...task,
              taskHistory: regularTask.taskHistory || task.taskHistory || []
            } as Task;
          } catch (e) {
            // If both fail, use the task we have (might not have latest history)
            console.warn('Could not refresh task history, using cached data:', e);
            refreshedTask = task;
          }
        }
      }
      
      console.log('Final task history count:', refreshedTask.taskHistory?.length || 0);
      setSelectedTaskForHistory(refreshedTask);
      setIsHistoryModalVisible(true);
    } catch (error) {
      console.error('Error loading task history:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : String(error),
        taskId: task.id,
        taskNumber: task.taskNumber,
        taskType: task.type
      });
      // Fallback to using the task we have
      setSelectedTaskForHistory(task);
      setIsHistoryModalVisible(true);
    }
  };

  const handleHistoryCommentAdded = (updatedTask: Task) => {
    // Update the task in the project's tasks array
    const updatedTasks = project.tasks?.map(t => 
      t.id === updatedTask.id ? { ...t, taskHistory: updatedTask.taskHistory } : t
    ) || [];
    project.tasks = updatedTasks;
    // Trigger a re-render
    onTaskSaved(updatedTask);
  };

  const handleConfirmCancel = async () => {
    try {
      setActionError(null);
      if (selectedTaskForCancel && currentUser?.id) {
        await taskService.cancelTask(selectedTaskForCancel.id.toString(), 'Task cancelled from Project Tasks', currentUser.id);
        
        // Update the task in the project's tasks array
        const updatedTasks = project.tasks?.map(t => 
          t.id === selectedTaskForCancel.id ? { ...t, status: 'CANCELLED' } : t
        ) || [];
        project.tasks = updatedTasks;
        
        // Trigger a re-render
        onTaskSaved(selectedTaskForCancel);
      }
    } catch (error) {
      console.error('Error cancelling task:', error);
      // describeActionError surfaces the server's reason (e.g. a linked Work ID blocks the cancel).
      setActionError(describeActionError(error, "Couldn't cancel the task. Try again."));
    } finally {
      setIsCancelTaskModalVisible(false);
      setSelectedTaskForCancel(null);
    }
  };

  const handlePrintTaskPDF = (task: Task) => {
    setPrintPdfTask(task);
  };

  return (
    <div ref={listContainerRef}>
      {/* Search/filter row didn't wrap at all, so on a phone the select + checkbox pushed Add
          Task straight off the right edge — the "add task button is overflowing" report. Stack
          below sm and let the search controls themselves wrap once they no longer fit one line. */}
      <div className="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between sm:tw-items-center tw-gap-3 tw-mb-3">
        <div className="tw-flex tw-flex-wrap tw-gap-2 tw-grow" style={{ maxWidth: '800px' }}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Search by Task ID or Name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>

          <Form.Select
            style={{ maxWidth: '200px' }}
            value={filterTaskType}
            onChange={(e) => setFilterTaskType(e.target.value as TaskType | '')}
          >
            <option value="">All Types</option>
            {getAvailableTaskTypes().map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </Form.Select>

          <Form.Check
            type="checkbox"
            id="showCanceledTasks"
            checked={showCanceledTasks}
            onChange={(e) => setShowCanceledTasks(e.target.checked)}
            label="Show Canceled Tasks"
            className="tw-whitespace-nowrap"
          />
        </div>

        {canAddTaskToThisProject && (
          <Button variant="primary" className="sm:tw-ml-3" onClick={handleAddTask}>
            <i className="bi bi-plus-lg tw-mr-2"></i>Add Task
          </Button>
        )}
      </div>


      {actionError && (
        <Alert variant="danger" role="alert" dismissible onClose={() => setActionError(null)} className="tw-mb-2">
          {actionError}
        </Alert>
      )}

      {/* Warning when project has no tasks - only for active projects */}
      {(!project.tasks || project.tasks.length === 0) && project.status.toLowerCase() === 'active' && (
        <Alert variant="warning" className="tw-flex tw-items-center tw-mb-4" role="alert">
          <i className="bi bi-exclamation-triangle-fill tw-mr-2"></i>
          <div>
            <strong>No Tasks Found</strong>
            <div className="tw-text-sm">This project doesn't have any tasks yet. Click "Add Task" to create the first task for this project.</div>
          </div>
        </Alert>
      )}

      {stickyScrollWidth > 0 && (
        <div
          ref={stickyScrollbarRef}
          onScroll={handleStickyScroll}
          style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            height: 14,
            backgroundColor: 'var(--bs-body-bg, #fff)',
            borderBottom: '1px solid rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ width: stickyScrollWidth, height: 1 }} />
        </div>
      )}
      <div
        ref={tableWrapperRef}
        className="tw-overflow-x-auto"
        onScroll={handleTableWrapperScroll}
      >
        <Table style={{ tableLayout: 'fixed', minWidth: '100%', width: 'max-content' }} hover>
          <thead className="table-sticky-header">
            <tr>
              <SortableTh
                label="Task ID"
                sortKey="taskNumber"
                activeKey={sortField}
                direction={sortDirection}
                onSort={handleSort}
                style={{ width: getColumnWidth('taskNumber'), position: 'relative' }}
              >
                <ResizeHandle columnId="taskNumber" currentWidthPx={getColumnWidthPx('taskNumber')} onResizeStart={startResize} />
              </SortableTh>
              <SortableTh
                label="Name"
                sortKey="name"
                activeKey={sortField}
                direction={sortDirection}
                onSort={handleSort}
                style={{ width: getColumnWidth('name'), position: 'relative' }}
              >
                <ResizeHandle columnId="name" currentWidthPx={getColumnWidthPx('name')} onResizeStart={startResize} />
              </SortableTh>
              <SortableTh
                label="Status"
                sortKey="status"
                activeKey={sortField}
                direction={sortDirection}
                onSort={handleSort}
                style={{ width: getColumnWidth('status'), position: 'relative' }}
                className="tw-text-center"
              >
                <ResizeHandle columnId="status" currentWidthPx={getColumnWidthPx('status')} onResizeStart={startResize} />
              </SortableTh>
              <SortableTh
                label="Type"
                sortKey="type"
                activeKey={sortField}
                direction={sortDirection}
                onSort={handleSort}
                style={{ width: getColumnWidth('type'), position: 'relative' }}
                className="tw-text-center"
              >
                <ResizeHandle columnId="type" currentWidthPx={getColumnWidthPx('type')} onResizeStart={startResize} />
              </SortableTh>
              <th style={{ width: getColumnWidth('coding'), position: 'relative' }} className="tw-text-center">
                <InfoTooltip
                  content={
                    <div>
                      <div><strong>Coding Formula:</strong></div>
                      <div style={{ marginTop: '4px' }}>
                        If a task belongs to a project that has a job number, the task is coded to the job number. Otherwise, the task is coded to its assigned activity number.
                      </div>
                      {project.tasks?.some((task) =>
                        task.type === 'Custom' && !task.project?.jobNumber
                      ) && (
                        <div style={{ marginTop: '8px', fontSize: '0.9rem', color: '#ffc107' }}>
                          <strong>Note:</strong> For Custom tasks without job numbers, "ZZZ-ZZZZ" is used as a temporary coding number.
                        </div>
                      )}
                    </div>
                  }
                  id="coding-tooltip-project-details"
                  placement="top"
                  maxWidth="400px"
                >
                  <span>
                    Coding <i className="bi bi-info-circle tw-ml-1" style={{ fontSize: '0.85rem', opacity: 0.7 }}></i>
                  </span>
                </InfoTooltip>
                <ResizeHandle columnId="coding" currentWidthPx={getColumnWidthPx('coding')} onResizeStart={startResize} />
              </th>
              <th style={{ width: getColumnWidth('activity'), position: 'relative' }} className="tw-text-center">
                Activity
                <ResizeHandle columnId="activity" currentWidthPx={getColumnWidthPx('activity')} onResizeStart={startResize} />
              </th>
              <th style={{ width: getColumnWidth('actions'), position: 'relative' }} className="tw-text-right">
                Actions
                <ResizeHandle columnId="actions" currentWidthPx={getColumnWidthPx('actions')} onResizeStart={startResize} />
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedTasks.map((task) => (
              <React.Fragment key={task.id}>
                <tr className="tw-align-middle">
                  <td>
                    {/* A label here (the row actions open the task), so plain text, not a dark pill. */}
                    <IdText>{task.taskNumber || <EmptyValue />}</IdText>
                  </td>
                  <td className="tw-break-words">
                    {task.name || <EmptyValue />}
                  </td>
                  <td className="tw-text-center">
                    <StatusBadge status={task.status} />
                  </td>
                  <td className="tw-text-center">
                    <Badge pill bg={
                      task.type === 'Quantified'
                        ? 'info'
                        : task.type === 'Routine'
                        ? 'warning'
                        : 'secondary'
                    }>
                      {task.type}
                    </Badge>
                  </td>
                  <td className="tw-text-center">
                    {(() => {
                      // If task has a project with job number, display job number
                      // Otherwise, display activity number
                      if (task.project?.jobNumber) {
                        return (
                          <IdText>{task.project.jobNumber}</IdText>
                        );
                      } else if (task.activities && task.activities.length > 0) {
                        return (
                          <IdText>{task.activities[0].activityNumber}</IdText>
                        );
                      } else {
                        return <EmptyValue />;
                      }
                    })()}
                  </td>
                  <td className="tw-text-center">
                    {(task.type === 'Quantified' || task.type === 'Routine' || task.type === 'Overhead') && task.activities && task.activities.length > 0 ? (
                      <div className="tw-flex tw-flex-col tw-gap-1">
                        {task.activities.map((activity, index) => (
                          <span key={activity.id || index} className="tw-text-sm">
                            {activity.activityNumber} - {activity.activityName}
                          </span>
                        ))}
                      </div>
                    ) : (task.type === 'Quantified' || task.type === 'Routine' || task.type === 'Overhead') ? (
                      <EmptyValue />
                    ) : null}
                  </td>
                  <td className="tw-text-right">
                    <div className="tw-flex tw-gap-2 tw-justify-end tw-items-center">
                      {task.efforts && task.efforts.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline-primary"
                          active={expandedRows.includes(task.id)}
                          onClick={() => toggleEfforts(task.id)}
                          title={expandedRows.includes(task.id) ? "Hide Efforts" : "Show Efforts"}
                        >
                          <i className={`bi bi-chevron-${expandedRows.includes(task.id) ? 'up' : 'down'}`}></i>
                        </Button>
                      )}
                      <TaskActionsDropdown
                        task={task}
                        canEditTask={canEditTask}
                        canCloneTask={canCloneTask}
                        canCloseTask={canCloseTask}
                        canReopenTask={canReopenTask}
                        canCancelTask={canCancelTask}
                        onViewHistory={() => handleViewHistory(task)}
                        onEditTask={() => handleEditTask(task)}
                        onCloneTask={() => setCloneConfirmTask(task)}
                        onCloseTask={() => handleCloseTask(task)}
                        onReopenTask={() => handleReopenTask(task)}
                        onCancelTask={() => handleCancelTask(task)}
                        onPrintPDF={() => handlePrintTaskPDF(task)}
                        onConvertToQuantified={() => handleConvertToQuantified(task)}
                        onConvertToCustom={() => handleConvertToCustom(task)}
                        onReportAccomplishment={() => handleReportAccomplishment(task)}
                        onSetInternalReview={() => handleSetInternalReview(task)}
                        onSetActive={() => handleSetActive(task)}
                      />
                    </div>
                  </td>
                </tr>
                {expandedRows.includes(task.id) && (
                  <tr>
                    <td colSpan={6} className="tw-p-0">
                      <div className="tw-border-t-0 tw-p-3 tw-bg-surface-alt">
                        <TaskEffortsTable
                          efforts={task.efforts || []} 
                          columns={{
                            poNumber: true, // Show PO column only on Project screen
                            fuelSurcharge: true
                          }}
                          projectMarkups={{
                            vehicleMarkup: project.vehicleMarkup,
                            materialMarkup: project.materialMarkup,
                            labourMarkup: project.labourMarkup,
                            subcontractorMarkup: project.subcontractorMarkup
                          }}
                          canToggleVehicleFuel={canToggleVehicleFuel}
                          onVehicleFuelEffortPatched={handleVehicleFuelEffortPatched}
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {sortedTasks.length === 0 && project.tasks && project.tasks.length > 0 && (
              <tr>
                <td colSpan={7} className="tw-text-center tw-text-content-muted tw-py-4">
                  <i className="bi bi-search tw-text-xl tw-block tw-mb-2"></i>
                  No tasks match your current filters
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <GenericTaskModal
        visible={showTaskModal && selectedTaskType === 'Overhead'}
        onClose={() => {
          setShowTaskModal(false);
          setSelectedTask(null);
        }}
        onTaskSaved={handleTaskSaved}
        projectId={project.id}
        task={selectedTask || undefined}
        defaultStatus={!selectedTask ? getDefaultTaskStatus() : undefined}
      />

      <RoutineTaskModal
        visible={showRoutineTaskModal}
        onClose={() => {
          setShowRoutineTaskModal(false);
          setSelectedRoutineTask(null);
        }}
        task={selectedRoutineTask || undefined}
        onTaskSaved={handleTaskSaved}
        project={project}
        defaultStatus={!selectedRoutineTask ? getDefaultTaskStatus() : undefined}
      />

      <TaskModal
        visible={showTaskModal}
        onClose={() => {
          setShowTaskModal(false);
          setSelectedTask(null);
        }}
        task={selectedTask || undefined}
        onTaskSaved={handleTaskSaved}
        foremanAreas={foremanAreas}
        taskType={selectedTask?.type || selectedTaskType}
        project={project}
        initialContractAreas={contractAreas}
        defaultStatus={!selectedTask ? getDefaultTaskStatus() : undefined}
      />

      <CustomTaskModal
        visible={showCustomTaskModal}
        onClose={() => {
          setShowCustomTaskModal(false);
          setSelectedTask(null);
        }}
        onSave={handleTaskSaved}
        task={selectedTask || null}
        project={project}
        defaultStatus={!selectedTask ? getDefaultTaskStatus() : undefined}
      />

      <TaskTypeSelector
        visible={showTypeSelector}
        onClose={() => setShowTypeSelector(false)}
        onSelect={handleTaskTypeSelected}
        availableTypes={getAvailableTaskTypes()}
      />

      {selectedTaskForClose && (
        <CloseRoutineTaskModal
          visible={isCloseTaskModalVisible}
          onClose={() => {
            setIsCloseTaskModalVisible(false);
            setSelectedTaskForClose(null);
          }}
          onSubmit={handleConfirmClose}
          task={selectedTaskForClose}
        />
      )}

      {selectedTaskForQuantifiedClose && (
        <CloseTaskModal
          visible={isQuantifiedCloseModalVisible}
          onClose={() => {
            setIsQuantifiedCloseModalVisible(false);
            setSelectedTaskForQuantifiedClose(null);
          }}
          task={selectedTaskForQuantifiedClose}
          onSubmit={handleConfirmQuantifiedClose}
        />
      )}

      {selectedTaskForCompletion && (
        <CompletionModal
          visible={isCompletionModalVisible}
          onClose={() => {
            setIsCompletionModalVisible(false);
            setSelectedTaskForCompletion(null);
          }}
          task={selectedTaskForCompletion}
          onComplete={handleCompletionDone}
        />
      )}

      {selectedTaskForReopen && (
        <ReopenTaskModal
          visible={isReopenTaskModalVisible}
          onClose={() => {
            setIsReopenTaskModalVisible(false);
            setSelectedTaskForReopen(null);
          }}
          task={selectedTaskForReopen}
          onSubmit={handleConfirmQuantifiedReopen}
        />
      )}

      {selectedTaskForInternalReview && (
        <SetInternalReviewModal
          visible={isSetInternalReviewModalVisible}
          onClose={() => {
            setIsSetInternalReviewModalVisible(false);
            setSelectedTaskForInternalReview(null);
          }}
          task={selectedTaskForInternalReview}
          onSubmit={async () => {
            handleInternalReviewSubmitted();
          }}
        />
      )}

      <CloneTaskConfirmModal
        show={cloneConfirmTask !== null}
        onHide={() => setCloneConfirmTask(null)}
        onConfirm={(names, quantifiedOptions) => {
          const t = cloneConfirmTask;
          setCloneConfirmTask(null);
          if (t) void handleCloneTask(t, names[0], quantifiedOptions);
        }}
        items={
          cloneConfirmTask
            ? [
                {
                  taskId: cloneConfirmTask.id,
                  sourceTaskLabel: cloneConfirmTask.taskNumber || `#${cloneConfirmTask.id}`,
                  sourceTaskName: cloneConfirmTask.name,
                  taskType: cloneConfirmTask.type,
                  completionAccomplishment: cloneConfirmTask.completionAccomplishment ?? 0,
                  contractYear: cloneConfirmTask.contractYear,
                  serviceAreaId:
                    cloneConfirmTask.foremanArea?.serviceAreaId ??
                    cloneConfirmTask.project?.serviceAreaId ??
                    project?.serviceAreaId ??
                    null
                }
              ]
            : []
        }
      />

      <CancelTaskConfirmationModal
        visible={isCancelTaskModalVisible}
        onClose={() => {
          setIsCancelTaskModalVisible(false);
          setSelectedTaskForCancel(null);
        }}
        onConfirm={handleConfirmCancel}
        taskName={selectedTaskForCancel?.name || selectedTaskForCancel?.taskNumber}
      />

      <ConvertTaskTypeModal
        visible={isConvertModalVisible}
        task={selectedTaskForConvert}
        direction={convertDirection}
        onClose={() => {
          setIsConvertModalVisible(false);
          setSelectedTaskForConvert(null);
          setConvertDirection(null);
        }}
        onConfirm={handleConfirmConvert}
      />

      {selectedTaskForHistory && (
        <TaskHistoryModal
          visible={isHistoryModalVisible}
          onClose={() => {
            setIsHistoryModalVisible(false);
            setSelectedTaskForHistory(null);
          }}
          task={selectedTaskForHistory}
          onAddComment={handleHistoryCommentAdded}
        />
      )}

      <PrintTaskPDFModal
        task={printPdfTask}
        onClose={() => setPrintPdfTask(null)}
      />
    </div>
  );
};

export default ProjectTasksTab; 